/**
 * Copies text, on plain HTTP as well as HTTPS.
 *
 * navigator.clipboard only exists in a secure context — HTTPS or localhost — so
 * on a dashboard served over http:// against a bare IP it is simply undefined
 * and the modern call would throw. The execCommand path is deprecated but still
 * works everywhere, so it stands in rather than leaving the button dead in the
 * one environment this app is actually deployed to.
 */
export async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard !== undefined && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);

      return true;
    } catch {
      // Permission refused, or the document was not focused. Fall through.
    }
  }

  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  const holder = document.createElement('textarea');

  holder.value = text;
  // Off-screen rather than hidden: execCommand ignores an unrenderable element,
  // and readOnly stops mobile keyboards appearing for the instant it exists.
  holder.setAttribute('readonly', '');
  holder.style.position = 'fixed';
  holder.style.top = '-1000px';
  holder.style.opacity = '0';

  document.body.appendChild(holder);

  const selection = document.getSelection();
  const previous = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  holder.select();

  let copied = false;

  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }

  holder.remove();

  // Putting the user's own selection back, since selecting the holder cleared it.
  if (previous && selection) {
    selection.removeAllRanges();
    selection.addRange(previous);
  }

  return copied;
}
